import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { GetClinicChannelStatusesQuery, useGetClinicChannelStatusesLazyQuery } from 'shared/types/graphql';
import { Square } from '@televet/kibble-ui';

interface FilterAppointmentsMenuProps {
  clinicId?: string;
  selectedChannelStatusIds: string[];
  setSelectedChannelStatusIds: Dispatch<SetStateAction<string[]>>;
}

export type ChannelStatusOption = NonNullable<GetClinicChannelStatusesQuery['findManyChannelStatus']>[0];

const FilterAppointmentsMenu = ({
  setSelectedChannelStatusIds,
  clinicId,
  selectedChannelStatusIds,
}: FilterAppointmentsMenuProps): JSX.Element => {
  const [getConversationStatuses, { data: channelStatusesData, loading: isLoadingChannelStatusData }] =
    useGetClinicChannelStatusesLazyQuery({
      variables: { clinicId: clinicId || '' },
    });

  const toggleChannelStatusFilter = useCallback(
    (value) => {
      const selectedIds = value.map((item: MenuItemProps) => {
        if (item && typeof item.value === 'string') return item.value;
        return;
      });
      setSelectedChannelStatusIds(selectedIds);
    },
    [setSelectedChannelStatusIds],
  );

  const conversationStatusOptions = useMemo(
    () =>
      channelStatusesData?.findManyChannelStatus?.map(
        (status: ChannelStatusOption): MenuItemProps => ({
          label: status.name,
          value: status.id,
          isSelected: selectedChannelStatusIds?.includes(status.id),
          groupLabel: status.channelStatusAction,
          leftElement: <Square size={3} borderRadius="sm" bg={status.color} />,
        }),
      ),
    [channelStatusesData?.findManyChannelStatus, selectedChannelStatusIds],
  );

  const isFiltering = useMemo(() => !!selectedChannelStatusIds.length, [selectedChannelStatusIds]);

  return (
    <Menu
      containerProps={{
        onOpen: getConversationStatuses,
        onClose: (value): void => toggleChannelStatusFilter(value),
      }}
      buttonProps={{
        variant: isFiltering ? 'ghost' : 'secondarySubtle',
        ...(isFiltering && { bg: 'background.secondaryAlpha.hover' }),
        size: 'sm',
        text: 'Filter All Appointments by Status',
        leftIconName: 'filter',
        showRightIcon: false,
      }}
      listProps={{
        isGrouped: true,
        isMultiSelect: true,
        isSearchable: true,
        menuItems: conversationStatusOptions,
        isLoading: isLoadingChannelStatusData,
      }}
    />
  );
};

export default FilterAppointmentsMenu;
